<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useCart } from '../stores/cartStore'

const { clearCart } = useCart()

const router = useRouter()
const user = ref(null)
const orders = ref([])
const transactions = ref([])
const deliveries = ref([])
const userWithdrawals = ref([])
const userWithdrawalMap = computed(() => {
  const m = new Map()
  for (const w of userWithdrawals.value) {
    if (w?.withdrawalId) m.set(w.withdrawalId, w)
  }
  return m
})
function txnHasInvoice(txn) {
  if (!txn || txn.type !== 'token_withdrawal' || !txn.relatedOrderId) return false
  const w = userWithdrawalMap.value.get(txn.relatedOrderId)
  return !!w && w.status === 'approved'
}
async function openWithdrawalInvoice(txn) {
  const w = userWithdrawalMap.value.get(txn.relatedOrderId)
  if (!w) return
  try {
    const res = await axios.get(
      `${API_URL}/api/user/withdrawals/${encodeURIComponent(w.withdrawalId)}/invoice/pdf`,
      { headers: getAuthHeaders(), responseType: 'blob' }
    )
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoice-${w.withdrawalId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch (err) {
    console.error('Failed to download invoice:', err)
    alert(err.response?.data?.message || 'Failed to download invoice')
  }
}
const activeTab = ref('orders')
const loading = ref(true)
const API_URL = import.meta.env.VITE_API_URL
const verifySending = ref(false)
const verifyMsg = ref('')

// Delete account state
const showDeleteForm = ref(false)
const deletePassword = ref('')
const deleteLoading = ref(false)
const deleteError = ref('')
const deleteMsg = ref(null)

const resendVerification = async () => {
  verifySending.value = true; verifyMsg.value = ''
  try {
    const token = localStorage.getItem('steam_token')
    const res = await axios.post(`${API_URL}/api/auth/send-verification`, {}, { headers: { Authorization: `Bearer ${token}` } })
    verifyMsg.value = res.data.sent ? 'Sent!' : (res.data.message || 'Failed')
  } catch (err) { verifyMsg.value = err.response?.data?.message || 'Could not send' }
  verifySending.value = false
  setTimeout(() => { verifyMsg.value = '' }, 3000)
}

const connectSteam = () => {
  const token = localStorage.getItem('steam_token')
  if (token) {
    window.location.href = `${API_URL}/api/auth/steam/link?token=${token}`
  }
}

const handleDeleteAccount = async () => {
  if (!confirm('Are you sure you want to delete your account? This action cannot be undone.')) return
  deleteLoading.value = true
  deleteError.value = ''
  try {
    const token = localStorage.getItem('steam_token')
    await axios.delete(`${API_URL}/api/auth/delete-account`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { password: deletePassword.value }
    })
    localStorage.removeItem('steam_token')
    clearCart()
    window.location.href = '/'
  } catch (err) {
    deleteError.value = err.response?.data?.error || 'Failed to delete account'
  } finally {
    deleteLoading.value = false
  }
}

// Name change state
const showNameForm = ref(false)
const nameForm = reactive({ name: '', surname: '' })
const nameLoading = ref(false)
const nameMsg = ref(null)

const toggleNameForm = async () => {
  showNameForm.value = !showNameForm.value
  nameMsg.value = null
  if (showNameForm.value && !nameForm.name && !nameForm.surname) {
    try {
      const res = await axios.get(`${API_URL}/api/user/name`, { headers: getAuthHeaders() })
      nameForm.name = res.data.name || ''
      nameForm.surname = res.data.surname || ''
    } catch (e) {}
  }
}

const handleNameChange = async () => {
  nameMsg.value = null
  if (!nameForm.name.trim() || !nameForm.surname.trim()) {
    nameMsg.value = { type: 'error', text: 'Both fields are required' }
    return
  }
  nameLoading.value = true
  try {
    const res = await axios.put(`${API_URL}/api/user/name`, {
      name: nameForm.name,
      surname: nameForm.surname
    }, { headers: getAuthHeaders() })
    if (res.data.success) {
      nameMsg.value = { type: 'success', text: 'Name updated successfully' }
      showNameForm.value = false
    } else {
      nameMsg.value = { type: 'error', text: res.data.message || 'Failed to update name' }
    }
  } catch (e) {
    nameMsg.value = { type: 'error', text: e.response?.data?.message || 'Failed to update name' }
  } finally {
    nameLoading.value = false
  }
}

// Withdrawal state — payout details (card/IBAN) are now collected on the
// GATEWAY hosted form, NOT in our UI. We only show amount input + Continue.
const showWithdrawModal = ref(false)
const withdrawStep = ref('amount') // 'amount' | 'confirm' | 'redirecting'
const withdrawAmount = ref('')
const withdrawError = ref('')
const withdrawLoading = ref(false)

const TOKENS_PER_EUR = 100
const MIN_EUR = 5
const MIN_TOKENS = Math.ceil(MIN_EUR * TOKENS_PER_EUR)

// Global toggle — hide AT/withdraw UI entirely if admin disabled payouts.
const withdrawalsEnabled = ref(true)

// When AT is disabled, also hide AT-related transactions from the history table.
const AT_TXN_TYPES = ['token_withdrawal', 'token_withdrawal_refund', 'admin_token_grant']
const visibleTransactions = computed(() =>
  withdrawalsEnabled.value
    ? transactions.value
    : transactions.value.filter(t => !AT_TXN_TYPES.includes(t.type))
)

// Inventory state
const showInventoryModal = ref(false)
const inventoryItems = ref([])
const inventoryLoading = ref(false)
const inventoryError = ref('')
const inventoryRemoving = ref(null) // blueprint of item being removed
const inventorySearch = ref('')
const removeConfirm = ref(null) // { name, blueprint, quantity } item pending confirmation
const removeCount = ref(1)

// Copy ID
const maskId = (id) => id ? id.slice(0, 4) + '***' : ''
const copiedId = ref(null)
const copyToClipboard = async (text, id) => {
  try {
    await navigator.clipboard.writeText(text)
    copiedId.value = id
    setTimeout(() => { if (copiedId.value === id) copiedId.value = null }, 1500)
  } catch (e) {
    console.error('Copy failed', e)
  }
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('steam_token')
  return { Authorization: `Bearer ${token}` }
}

const openTopUp = () => {
  window.dispatchEvent(new CustomEvent('open-topup'))
}

const openInvoice = async (orderId) => {
  if (!orderId) return
  try {
    const res = await axios.get(`${API_URL}/api/payments/invoice/${encodeURIComponent(orderId)}/pdf`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoice-${orderId}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 5000)
  } catch (err) {
    console.error('Failed to download invoice:', err)
    alert(err.response?.data?.message || 'Failed to download invoice')
  }
}

// Inventory functions
const openInventory = () => {
  inventoryError.value = ''
  inventorySearch.value = ''
  removeConfirm.value = null
  removeCount.value = 1
  showInventoryModal.value = true
  fetchInventory()
}

const fetchInventory = async () => {
  inventoryLoading.value = true
  inventoryError.value = ''
  try {
    const res = await axios.get(`${API_URL}/api/user/inventory`, { headers: getAuthHeaders() })
    inventoryItems.value = res.data.items || res.data || []
  } catch (e) {
    inventoryError.value = e.response?.data?.message || 'Failed to load inventory. Make sure you are online in-game.'
    inventoryItems.value = []
  } finally {
    inventoryLoading.value = false
  }
}

const filteredInventory = computed(() => {
  if (!inventorySearch.value.trim()) return inventoryItems.value
  const q = inventorySearch.value.toLowerCase()
  return inventoryItems.value.filter(item =>
    (item.name && item.name.toLowerCase().includes(q)) ||
    (item.blueprint && item.blueprint.toLowerCase().includes(q))
  )
})

const askRemoveItem = (item) => {
  removeConfirm.value = { ...item }
  removeCount.value = 1
}

const cancelRemove = () => {
  removeConfirm.value = null
  removeCount.value = 1
}

const confirmRemoveItem = async () => {
  if (!removeConfirm.value) return
  const { blueprint } = removeConfirm.value
  const count = parseInt(removeCount.value) || 1
  inventoryRemoving.value = blueprint
  inventoryError.value = ''
  try {
    await axios.post(`${API_URL}/api/user/inventory/remove`, { blueprint, count }, { headers: getAuthHeaders() })
    removeConfirm.value = null
    removeCount.value = 1
    await fetchInventory()
  } catch (e) {
    inventoryError.value = e.response?.data?.message || 'Failed to remove item'
  } finally {
    inventoryRemoving.value = null
  }
}

const closeInventoryModal = () => {
  showInventoryModal.value = false
  removeConfirm.value = null
}

const shortBlueprintName = (blueprint) => {
  if (!blueprint) return ''
  const match = blueprint.match(/\.([^.]+)(?:'|$)/)
  return match ? match[1] : blueprint.split('/').pop()?.split('.').pop() || blueprint
}

const logout = () => {
  localStorage.removeItem('steam_token')
  clearCart()
  window.location.href = '/'
}

const openWithdraw = async () => {
  withdrawError.value = ''
  withdrawAmount.value = ''
  withdrawLoading.value = false
  withdrawStep.value = 'amount'
  showWithdrawModal.value = true
}

const submitWithdraw = async () => {
  withdrawError.value = ''
  const amount = parseFloat(withdrawAmount.value)

  if (!amount || amount <= 0) {
    withdrawError.value = 'Enter a valid amount'
    return
  }
  const userTokens = parseFloat(user.value?.activity_tokens) || 0
  if (amount > userTokens) {
    withdrawError.value = 'Insufficient Activity Points'
    return
  }
  if (amount < MIN_TOKENS) {
    withdrawError.value = `Minimum withdrawal is ${MIN_EUR} EUR (${MIN_TOKENS} AP)`
    return
  }

  withdrawStep.value = 'confirm'
}

// Final confirm — server initiates GATEWAY payout and returns redirect-url
// where player completes card entry on the gateway's hosted form.
const confirmWithdraw = async () => {
  withdrawError.value = ''
  withdrawLoading.value = true

  try {
    const res = await axios.post(`${API_URL}/api/user/withdraw`, {
      tokenAmount: parseFloat(withdrawAmount.value)
    }, { headers: getAuthHeaders() })

    if (res.data.newTokenBalance !== undefined) {
      user.value.activity_tokens = res.data.newTokenBalance
    } else if (res.data.newBalance !== undefined) {
      user.value.activity_tokens = res.data.newBalance
    }

    if (res.data.redirectUrl) {
      withdrawStep.value = 'redirecting'
      // Hand off to GATEWAY hosted form — card details are entered on their domain
      window.location.href = res.data.redirectUrl
      return
    }
    withdrawError.value = 'Gateway did not return a redirect URL'
    withdrawStep.value = 'amount'
  } catch (e) {
    withdrawError.value = e.response?.data?.message || 'Withdrawal failed'
    withdrawStep.value = 'amount'
  } finally {
    withdrawLoading.value = false
  }
}

const closeWithdrawModal = () => {
  showWithdrawModal.value = false
}

onMounted(async () => {
  const token = localStorage.getItem('steam_token')
  if (!token) {
    router.push('/')
    return
  }

  try {
    const headers = { Authorization: `Bearer ${token}` }
    
    const [userRes, ordersRes, transactionsRes, deliveriesRes, withdrawalsRes, configRes] = await Promise.all([
      axios.get(`${API_URL}/api/auth/user`, { headers }),
      axios.get(`${API_URL}/api/user/orders`, { headers }),
      axios.get(`${API_URL}/api/user/transactions`, { headers }),
      axios.get(`${API_URL}/api/user/deliveries`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/user/withdrawals`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/withdrawals/config`).catch(() => ({ data: { enabled: true } })),
    ])

    if (userRes.data.user) {
      user.value = userRes.data.user
    } else {
      router.push('/')
      return
    }

    orders.value = ordersRes.data
    transactions.value = transactionsRes.data
    deliveries.value = deliveriesRes.data || []
    userWithdrawals.value = Array.isArray(withdrawalsRes.data) ? withdrawalsRes.data : []
    withdrawalsEnabled.value = configRes.data?.enabled !== false
  } catch (e) {
    console.error('Failed to load profile:', e)
    router.push('/')
  } finally {
    loading.value = false
  }
})

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusClass = (status) => {
  switch (status) {
    case 'completed': return 'status-completed'
    case 'pending': return 'status-pending'
    case 'failed': return 'status-failed'
    case 'rejected': return 'status-failed'
    default: return ''
  }
}

const getTransactionClass = (txn) => {
  if (txn.description && txn.description.toLowerCase().includes('failed')) return 'txn-failed'
  if (txn.type === 'withdrawal') return 'txn-withdrawal'
  if (txn.type === 'withdrawal_refund') return 'txn-topup'
  if (txn.type === 'token_withdrawal') return 'txn-token_withdrawal'
  if (txn.type === 'token_withdrawal_refund') return 'txn-token_withdrawal_refund'
  if (txn.type === 'admin_token_grant') return 'txn-admin_token_grant'
  return txn.type === 'topup' ? 'txn-topup' : 'txn-purchase'
}

const isTokenTxn = (txn) =>
  txn.type === 'token_withdrawal' ||
  txn.type === 'token_withdrawal_refund' ||
  txn.type === 'admin_token_grant'

const formatTxnAmount = (txn) => {
  const v = parseFloat(Number(txn.amount).toFixed(2))
  const unit = isTokenTxn(txn) ? 'AP' : 'ArkGem'
  return `${txn.amount > 0 ? '+' : ''}${v} ${unit}`
}

const formatTxnBalance = (txn) => {
  const v = parseFloat(Number(txn.balanceAfter).toFixed(2))
  return isTokenTxn(txn) ? `${v} AP` : `${v} ArkGem`
}

const txnTypeLabel = (txn) => {
  switch (txn.type) {
    case 'withdrawal_refund': return 'refund'
    case 'token_withdrawal': return 'AP withdrawal'
    case 'token_withdrawal_refund': return 'AP refund'
    case 'admin_token_grant': return 'AP grant'
    default: return txn.type
  }
}

const isFailedTxn = (txn) => {
  return txn.description && txn.description.toLowerCase().includes('failed')
}

const getTopupStatus = (txn) => {
  if (isFailedTxn(txn)) return { text: 'Failed', cls: 'delivery-failed' }
  return { text: 'Completed', cls: 'delivery-delivered' }
}

const getDeliveryForOrder = (orderId) => {
  if (!orderId) return null
  return deliveries.value.find(d => d.orderId === orderId)
}

const getDeliveryBadge = (status) => {
  switch (status) {
    case 'delivered': return { text: 'Delivered', cls: 'delivery-delivered' }
    case 'pending': return { text: 'Pending', cls: 'delivery-pending' }
    case 'failed': return { text: 'Failed', cls: 'delivery-failed' }
    case 'not_applicable': return null
    default: return null
  }
}

const getWithdrawalStatusBadge = (txn) => {
  if (txn.type === 'withdrawal_refund' || txn.type === 'token_withdrawal_refund') {
    return { text: 'Refunded', cls: 'delivery-delivered' }
  }
  if (txn.status === 'pending') return { text: 'Pending', cls: 'delivery-pending' }
  if (txn.status === 'completed') return { text: 'Paid', cls: 'delivery-delivered' }
  if (txn.status === 'rejected') return { text: 'Rejected', cls: 'delivery-failed' }
  return { text: txn.status, cls: 'delivery-pending' }
}
</script>

<template>
  <div class="profile-page">
    <div class="profile-container" v-if="!loading && user">
      <div class="profile-header">
        <div class="profile-user">
          <img :src="user.avatarFull || user.avatar" class="profile-avatar" />
          <div class="profile-info">
            <h1 class="profile-name">{{ user.displayName }}</h1>
            <div class="profile-ids">
              <button class="id-chip" @click="copyToClipboard(user.steamId, 'steam')" :title="copiedId === 'steam' ? 'Copied!' : 'Click to copy'">
                <span class="id-chip-label">Steam</span>
                <span class="id-chip-value">{{ maskId(user.steamId) }}</span>
                <svg v-if="copiedId !== 'steam'" class="id-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                <svg v-else class="id-chip-icon id-chip-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
              <button v-if="user.eosId" class="id-chip id-chip-eos" @click="copyToClipboard(user.eosId, 'eos')" :title="copiedId === 'eos' ? 'Copied!' : 'Click to copy'">
                <span class="id-chip-label">EOS</span>
                <span class="id-chip-value">{{ maskId(user.eosId) }}</span>
                <svg v-if="copiedId !== 'eos'" class="id-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                <svg v-else class="id-chip-icon id-chip-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
              <span v-else class="id-chip-unlinked">EOS not linked</span>
              <!-- Email Verification chip -->
              <span v-if="user.email && user.emailVerified" class="id-chip id-chip-verified">
                <span class="id-chip-label" style="color:#00d4ff;">Verified</span>
              </span>
              <span v-else-if="user.email && !user.emailVerified" class="id-chip id-chip-unverified" style="margin-right:8px;">
                <span class="id-chip-label" style="color:#f59e0b;">Unverified</span>
                <span v-if="verifyMsg" style="color:#00d4ff;font-size:10px;">{{ verifyMsg }}</span>
                <button v-else class="id-chip-resend" :disabled="verifySending" @click.stop="resendVerification">{{ verifySending ? '...' : 'resend' }}</button>
              </span>
            </div>
          </div>
        </div>
        <div class="profile-actions">
          <div class="profile-balance-box">
            <span class="balance-value">{{ parseFloat(Number(user.balance).toFixed(2)) }}</span>
            <span class="balance-label">ArkGem</span>
          </div>
          <button class="btn-action-profile btn-topup" @click="openTopUp">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
            TOP UP
          </button>
          <button v-if="withdrawalsEnabled" class="btn-action-profile btn-activity-tokens" @click="openWithdraw" title="Activity Points">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></svg>
            <span class="btn-at-label">
              <span class="btn-at-title">ACTIVITY POINTS</span>
              <span class="btn-at-balance">{{ parseFloat(Number(user.activity_tokens || 0).toFixed(2)) }} AP</span>
            </span>
          </button>
          <button class="btn-action-profile btn-inventory" @click="openInventory">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            INVENTORY
          </button>
          <button class="btn-action-profile btn-logout btn-logout-icon" @click="logout" title="Log out">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </div>

      <!-- Account Settings -->
      <div class="account-settings-section">
        <div v-if="nameMsg" :class="['ark-settings-msg', nameMsg.type === 'success' ? 'ark-msg-success' : 'ark-msg-error']">
          {{ nameMsg.text }}
        </div>
        <button type="button" class="ark-settings-toggle" @click="toggleNameForm">
          <span>Change Name</span>
          <svg :class="['ark-toggle-icon', { open: showNameForm }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
        <form v-if="showNameForm" @submit.prevent="handleNameChange" class="ark-settings-form">
          <input type="text" v-model="nameForm.name" placeholder="First name" />
          <input type="text" v-model="nameForm.surname" placeholder="Last name" />
          <button type="submit" :disabled="nameLoading" class="ark-settings-submit">
            {{ nameLoading ? 'Updating...' : 'Update Name' }}
          </button>
        </form>
      </div>

      <!-- Connect Steam (email users only) -->
      <div v-if="user && !user.hasSteam" class="account-settings-section" style="margin-top: 1rem;">
        <div class="ark-steam-connect-card">
          <div class="ark-steam-connect-info">
            <img src="/download.svg" alt="Steam" width="24" height="24" style="opacity: 0.7" />
            <div>
              <p style="color: #fff; font-size: 0.85rem; font-weight: 600; margin: 0;">Connect your Steam account</p>
              <p style="color: rgba(255,255,255,0.5); font-size: 0.75rem; margin: 0.25rem 0 0;">Required to purchase items from the market and receive in-game deliveries.</p>
            </div>
          </div>
          <button class="ark-steam-connect-btn" @click="connectSteam">
            <img src="/download.svg" alt="Steam" width="18" height="18" />
            Link Steam Account
          </button>
        </div>
      </div>

      <!-- Delete Account -->
      <div class="account-settings-section" style="margin-top: 1rem;">
        <div v-if="deleteMsg" :class="['ark-settings-msg', deleteMsg.type === 'success' ? 'ark-msg-success' : 'ark-msg-error']">
          {{ deleteMsg.text }}
        </div>
        <button type="button" class="ark-settings-toggle ark-settings-toggle-danger" @click="showDeleteForm = !showDeleteForm">
          <span>Delete ARK Account</span>
          <svg :class="['ark-toggle-icon', { open: showDeleteForm }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
        </button>
        <div v-if="showDeleteForm" class="ark-settings-form" style="padding: 0.75rem;">
          <p style="color: #e55; font-size: 0.8rem; margin: 0 0 0.75rem;">Your ArkGems balance, order history, and all account data will be permanently deleted.</p>
          <input v-if="user && !user.hasSteam" type="password" v-model="deletePassword" placeholder="Enter your password to confirm" style="width:100%;padding:0.65rem 0.75rem;border-radius:6px;border:1px solid rgba(255,68,68,0.3);background:rgba(5,5,15,0.8);color:#fff;font-size:0.85rem;margin-bottom:0.75rem;box-sizing:border-box" />
          <div v-if="deleteError" style="color:#e55;font-size:0.8rem;margin-bottom:0.5rem;padding:0.5rem;background:rgba(255,68,68,0.1);border-radius:4px">{{ deleteError }}</div>
          <button class="ark-delete-confirm-btn" :disabled="deleteLoading" @click="handleDeleteAccount">
            {{ deleteLoading ? 'Deleting...' : 'Delete Permanently' }}
          </button>
        </div>
      </div>

      <div class="history-section">
        <div class="history-header">
          <h3 class="history-title">TRANSACTION HISTORY</h3>
          <span class="history-count">{{ transactions.length }} transactions</span>
        </div>

        <div class="history-content">
          <div v-if="transactions.length === 0" class="empty-state">
            <p>No history yet</p>
          </div>
          <div v-else class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="txn in visibleTransactions" :key="txn.id" :class="{ 'row-failed': isFailedTxn(txn) }">
                  <td>
                    <span class="type-badge" :class="getTransactionClass(txn)">
                      {{ txnTypeLabel(txn) }}
                    </span>
                  </td>
                  <td>{{ txn.description }}</td>
                  <td :class="txn.amount > 0 ? 'amount-positive' : 'amount-negative'">
                    {{ formatTxnAmount(txn) }}
                  </td>
                  <td>{{ formatTxnBalance(txn) }}</td>
                  <td>
                    <template v-if="['withdrawal','withdrawal_refund','token_withdrawal','token_withdrawal_refund'].includes(txn.type)">
                      <span class="delivery-badge" :class="getWithdrawalStatusBadge(txn).cls">
                        {{ getWithdrawalStatusBadge(txn).text }}
                      </span>
                      <button
                        v-if="txnHasInvoice(txn)"
                        class="btn-invoice-row"
                        @click="openWithdrawalInvoice(txn)"
                        title="Download payout invoice"
                      >Invoice</button>
                    </template>
                    <template v-else-if="txn.type === 'purchase' && txn.relatedOrderId">
                      <span 
                        v-if="getDeliveryForOrder(txn.relatedOrderId)" 
                        class="delivery-badge"
                        :class="getDeliveryBadge(getDeliveryForOrder(txn.relatedOrderId).deliveryStatus)?.cls"
                      >
                        {{ getDeliveryBadge(getDeliveryForOrder(txn.relatedOrderId).deliveryStatus)?.text || '—' }}
                      </span>
                      <span v-else class="delivery-badge delivery-pending">Pending</span>
                    </template>
                    <template v-else-if="txn.type === 'topup'">
                      <span class="delivery-badge" :class="getTopupStatus(txn).cls">
                        {{ getTopupStatus(txn).text }}
                      </span>
                      <button
                        v-if="txn.status === 'completed' && txn.relatedOrderId"
                        class="btn-invoice-row"
                        @click="openInvoice(txn.relatedOrderId)"
                        title="Open invoice"
                      >Invoice</button>
                    </template>
                    <span v-else class="delivery-na">—</span>
                  </td>
                  <td class="date">{{ formatDate(txn.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="profile-loading">
      <div class="profile-loading-bar">
        <div class="profile-loading-bar-fill"></div>
      </div>
      <span class="profile-loading-text">LOADING PROFILE</span>
    </div>

    <!-- Withdrawal Modal -->
    <div v-if="showWithdrawModal" class="topup-overlay" @click.self="closeWithdrawModal">
      <div class="topup-modal withdraw-modal">
        <button class="topup-close" @click="closeWithdrawModal">&times;</button>

        <!-- Step: Enter Amount -->
        <template v-if="withdrawStep === 'amount'">
          <h2 class="topup-title">ACTIVITY POINTS</h2>
          <p class="at-modal-desc">
            Activity Points are a special in-game currency earned exclusively through completing
            quests, daily and seasonal activities, reporting bugs, and engaging with core gameplay
            systems. They represent active participation and progression within the game world.
          </p>

          <div class="at-rate-row">
            <div class="at-rate-pill">
              <span class="at-rate-label">RATE</span>
              <span class="at-rate-value">{{ TOKENS_PER_EUR }} AP = 1 EUR</span>
            </div>
            <div class="at-rate-pill">
              <span class="at-rate-label">MIN</span>
              <span class="at-rate-value">{{ MIN_EUR }} EUR ({{ MIN_TOKENS }} AP)</span>
            </div>
          </div>

          <div class="topup-form">
            <label class="topup-label">Amount (Activity Points)</label>
            <input
              type="number"
              v-model="withdrawAmount"
              placeholder="0"
              min="1"
              :max="user?.activity_tokens || 0"
              class="topup-input"
            />
            <div class="withdraw-balance-hint">
              Available: <strong>{{ parseFloat(Number(user?.activity_tokens || 0).toFixed(2)) }}</strong> AP
            </div>
          </div>

          <div class="topup-quick">
            <button class="topup-quick-btn" @click="withdrawAmount = Math.floor((user?.activity_tokens || 0) * 0.25)">25%</button>
            <button class="topup-quick-btn" @click="withdrawAmount = Math.floor((user?.activity_tokens || 0) * 0.5)">50%</button>
            <button class="topup-quick-btn" @click="withdrawAmount = Math.floor((user?.activity_tokens || 0) * 0.75)">75%</button>
            <button class="topup-quick-btn" @click="withdrawAmount = Math.floor(user?.activity_tokens || 0)">MAX</button>
          </div>

          <div v-if="withdrawError" class="withdraw-error">{{ withdrawError }}</div>

          <p class="withdraw-gateway-note">
            On confirm you'll be redirected to the payment gateway to enter your card details. We never see or store them.
          </p>

          <button class="btn-topup-submit btn-withdraw-submit" @click="submitWithdraw">
            Continue
          </button>
        </template>

        <!-- Step: Confirm -->
        <template v-else-if="withdrawStep === 'confirm'">
          <h2 class="topup-title">CONFIRM WITHDRAWAL</h2>

          <div class="withdraw-confirm-details">
            <div class="confirm-row">
              <span>Amount</span>
              <strong>{{ withdrawAmount }} AP</strong>
            </div>
          </div>

          <p class="withdraw-gateway-note">
            Click below to continue to the payment gateway where you enter the destination card details.
          </p>

          <div v-if="withdrawError" class="withdraw-error">{{ withdrawError }}</div>

          <div class="withdraw-confirm-actions">
            <button class="btn-secondary" @click="withdrawStep = 'amount'">Back</button>
            <button
              class="btn-topup-submit btn-withdraw-submit"
              @click="confirmWithdraw"
              :disabled="withdrawLoading"
            >
              {{ withdrawLoading ? 'Redirecting...' : 'Continue to Payment' }}
            </button>
          </div>
        </template>

        <!-- Step: Redirecting (brief in-between, normally invisible) -->
        <template v-else-if="withdrawStep === 'redirecting'">
          <h2 class="topup-title">REDIRECTING…</h2>
          <p class="withdraw-gateway-note">Taking you to the payment gateway…</p>
        </template>
      </div>
    </div>

    <!-- Inventory Modal -->
    <div v-if="showInventoryModal" class="topup-overlay" @click.self="closeInventoryModal">
      <div class="topup-modal inventory-modal">
        <button class="topup-close" @click="closeInventoryModal">&times;</button>

        <h2 class="topup-title">INVENTORY</h2>

        <!-- Top bar: search + refresh -->
        <div class="inv-toolbar">
          <input
            type="text"
            v-model="inventorySearch"
            placeholder="Search items..."
            class="inv-search-input"
          />
          <button class="inv-refresh-btn" @click="fetchInventory" :disabled="inventoryLoading">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :class="{ 'inv-spinning': inventoryLoading }"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0115.36-6.36L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 01-15.36 6.36L3 16"/></svg>
          </button>
        </div>

        <!-- Error -->
        <div v-if="inventoryError" class="withdraw-error">{{ inventoryError }}</div>

        <!-- Loading state -->
        <div v-if="inventoryLoading && inventoryItems.length === 0" class="inv-loading">
          <div class="profile-loading-bar"><div class="profile-loading-bar-fill"></div></div>
          <span class="inv-loading-text">LOADING ITEMS</span>
        </div>

        <!-- Empty state -->
        <div v-else-if="!inventoryLoading && inventoryItems.length === 0 && !inventoryError" class="inv-empty">
          <p>Inventory is empty or player is offline.</p>
        </div>

        <!-- Items list -->
        <div v-else class="inv-list">
          <div v-if="filteredInventory.length === 0" class="inv-empty">
            <p>No items matching "{{ inventorySearch }}"</p>
          </div>
          <div
            v-for="(item, idx) in filteredInventory"
            :key="idx"
            class="inv-item"
            :class="{ 'inv-item-removing': inventoryRemoving === item.blueprint }"
          >
            <div class="inv-item-info">
              <span class="inv-item-name">{{ item.name || shortBlueprintName(item.blueprint) }}</span>
              <span class="inv-item-blueprint" :title="item.blueprint">{{ shortBlueprintName(item.blueprint) }}</span>
            </div>
            <div class="inv-item-right">
              <span class="inv-item-qty">x{{ item.quantity || 1 }}</span>
              <button class="inv-remove-btn" @click="askRemoveItem(item)" :disabled="inventoryRemoving !== null">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Item count -->
        <div v-if="inventoryItems.length > 0" class="inv-footer">
          {{ filteredInventory.length }} / {{ inventoryItems.length }} items
        </div>

        <!-- Remove confirmation overlay -->
        <div v-if="removeConfirm" class="inv-confirm-overlay" @click.self="cancelRemove">
          <div class="inv-confirm-box">
            <h3 class="inv-confirm-title">REMOVE ITEM</h3>
            <p class="inv-confirm-name">{{ removeConfirm.name || shortBlueprintName(removeConfirm.blueprint) }}</p>
            <div class="inv-confirm-qty-row">
              <label>Count:</label>
              <input type="number" v-model.number="removeCount" min="1" :max="removeConfirm.quantity || 1" class="inv-confirm-input" />
              <button class="inv-confirm-max-btn" @click="removeCount = removeConfirm.quantity || 1">ALL</button>
            </div>
            <div v-if="inventoryError" class="withdraw-error" style="margin-top: 10px;">{{ inventoryError }}</div>
            <div class="inv-confirm-actions">
              <button class="btn-secondary" @click="cancelRemove">Cancel</button>
              <button class="inv-confirm-delete-btn" @click="confirmRemoveItem" :disabled="inventoryRemoving !== null">
                {{ inventoryRemoving ? 'Removing...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.profile-page {
  min-height: 100vh;
  padding: 120px 20px 40px;
  background: url('/profile-bg.jpg') center center / cover no-repeat fixed;
  position: relative;
}

.profile-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 0;
}

.profile-container {
  position: relative;
  z-index: 1;
}

.profile-container {
  max-width: 1200px;
  margin: 0 auto;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  background: rgba(10, 10, 20, 0.85);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  margin-bottom: 2rem;
}

.profile-user {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid var(--color-primary);
}

.profile-name {
  font-size: 1.5rem;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.profile-ids {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.id-chip-verified {
  border-color: rgba(0,212,255,0.2);
  background: rgba(0,212,255,0.06);
}

.id-chip-unverified {
  border-color: rgba(245,158,11,0.2);
  background: rgba(245,158,11,0.06);
  gap: 4px;
}

.id-chip-resend {
  font-size: 10px;
  color: #f59e0b;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-family: inherit;
}

.id-chip-resend:hover { color: #fbbf24; }
.id-chip-resend:disabled { opacity: 0.5; cursor: not-allowed; text-decoration: none; }

.id-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px 3px 6px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  max-width: 100%;
}

.id-chip:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.id-chip-label {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  flex-shrink: 0;
}

.id-chip-value {
  font-size: 0.75rem;
  font-family: monospace;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.id-chip-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.25);
  transition: color 0.2s;
}

.id-chip:hover .id-chip-icon {
  color: rgba(255, 255, 255, 0.5);
}

.id-chip-check {
  color: #2ecc71 !important;
}

.id-chip-eos .id-chip-label {
  color: rgba(138, 92, 246, 0.6);
}

.id-chip-unlinked {
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.25);
  padding: 3px 8px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.profile-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.profile-balance-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: rgba(0, 212, 255, 0.08);
  border: 1px solid rgba(0, 212, 255, 0.25);
  border-radius: 10px;
  height: 40px;
  box-sizing: border-box;
}

.balance-label {
  color: rgba(0, 212, 255, 0.5);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.balance-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-primary);
}

/* Uniform action buttons */
.btn-action-profile {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  border: 1px solid;
  height: 40px;
  box-sizing: border-box;
}

.btn-action-profile svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.btn-topup {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 153, 204, 0.08) 100%);
  border-color: rgba(0, 212, 255, 0.35);
  color: #00d4ff;
}

.btn-topup:hover {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.25) 0%, rgba(0, 153, 204, 0.15) 100%);
  border-color: rgba(0, 212, 255, 0.6);
  box-shadow: 0 4px 20px rgba(0, 212, 255, 0.2);
  transform: translateY(-1px);
}

.btn-logout-icon {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.35);
  padding: 10px;
  min-width: unset;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0;
}

.btn-logout-icon svg {
  width: 18px;
  height: 18px;
}

.btn-logout-icon:hover {
  background: rgba(231, 76, 60, 0.1);
  border-color: rgba(231, 76, 60, 0.3);
  color: #e74c3c;
  transform: translateY(-1px);
}

.history-section {
  background: rgba(10, 10, 20, 0.85);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  overflow: hidden;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: rgba(0, 212, 255, 0.05);
  border-bottom: 1px solid var(--color-border);
}

.history-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-primary);
  letter-spacing: 2px;
}

.history-count {
  font-size: 0.85rem;
  color: var(--color-text-muted);
}

.history-content {
  padding: 0;
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  overflow: hidden;
}

.data-table th,
.data-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
}

.data-table th {
  background: rgba(0, 0, 0, 0.5);
  color: var(--color-text-muted);
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.data-table td {
  color: var(--color-text);
}

.data-table tr:hover {
  background: rgba(0, 212, 255, 0.05);
}

.order-id, .txn-id {
  font-family: monospace;
  font-size: 0.85rem;
  color: var(--color-primary);
}

.total-price {
  font-weight: 600;
}

.date {
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.status-badge, .type-badge {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-completed {
  background: rgba(46, 204, 113, 0.2);
  color: #2ecc71;
}

.status-pending {
  background: rgba(241, 196, 15, 0.2);
  color: #f1c40f;
}

.status-failed {
  background: rgba(231, 76, 60, 0.2);
  color: #e74c3c;
}

.txn-failed {
  background: rgba(231, 76, 60, 0.2);
  color: #e74c3c;
}

.row-failed {
  background: rgba(231, 76, 60, 0.06);
}

.row-failed td {
  opacity: 0.75;
}

.txn-topup {
  background: rgba(46, 204, 113, 0.2);
  color: #2ecc71;
}

.txn-purchase {
  background: rgba(155, 89, 182, 0.2);
  color: #9b59b6;
}

.amount-positive {
  color: #2ecc71;
  font-weight: 600;
}

.amount-negative {
  color: #e74c3c;
  font-weight: 600;
}

.empty-state {
  padding: 3rem;
  text-align: center;
  color: var(--color-text-muted);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
}

.profile-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 40px;
  gap: 24px;
}

.profile-loading-bar {
  width: 100px;
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}

.profile-loading-bar-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  border-radius: 3px;
  animation: profile-slide 1.2s ease-in-out infinite;
}

@keyframes profile-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

.profile-loading-text {
  font-family: var(--font-display);
  font-size: 0.75rem;
  letter-spacing: 4px;
  color: rgba(255, 255, 255, 0.35);
}

@media (max-width: 768px) {
  .profile-main {
    padding-top: 80px;
    padding-left: 15px;
    padding-right: 15px;
  }
  
  .profile-header {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
    padding: 20px;
  }

  .profile-user {
    flex-direction: column;
  }

  .profile-ids {
    flex-direction: column;
    width: 100%;
  }

  .id-chip {
    width: 100%;
    justify-content: center;
  }

  .profile-balance-box {
    width: 100%;
    padding: 14px 18px;
    border-radius: 12px;
  }

  .balance-value {
    font-size: 1.4rem;
  }

  .balance-label {
    font-size: 0.75rem;
  }

  .profile-actions {
    flex-direction: column;
    width: 100%;
  }

  .btn-action-profile {
    width: 100%;
    justify-content: center;
    padding: 12px 18px;
    font-size: 0.75rem;
    height: 44px;
  }

  .profile-tabs {
    flex-direction: column;
    gap: 10px;
  }
  
  .tab-btn {
    padding: 12px 20px;
    font-size: 0.85rem;
  }
  
  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .data-table {
    min-width: 500px;
    font-size: 0.8rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 10px 8px;
    white-space: nowrap;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .transaction-count {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .profile-main {
    padding-top: 70px;
    padding-left: 10px;
    padding-right: 10px;
  }
  
  .profile-header {
    padding: 15px;
  }
  
  .user-avatar {
    width: 80px;
    height: 80px;
  }
  
  .user-name {
    font-size: 1.3rem;
  }
  
  .profile-balance-box {
    padding: 15px;
  }
  
  .balance-amount {
    font-size: 2rem;
  }
  
  .btn-logout-icon {
    width: 36px;
    height: 36px;
    padding: 8px;
  }
  
  .data-table {
    min-width: 450px;
    font-size: 0.75rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 6px;
  }
  
  .status-badge, .type-badge {
    padding: 3px 6px;
    font-size: 0.65rem;
  }
}

/* Top Up Modal */
.topup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
  padding: 20px;
  box-sizing: border-box;
}

.topup-modal {
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  border: 2px solid #333;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-sizing: border-box;
}

.topup-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #666;
  font-size: 28px;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
}

.topup-close:hover {
  color: var(--color-primary);
}

.topup-title {
  font-family: var(--font-display);
  font-size: 1.75rem;
  color: var(--color-primary);
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 2px;
}

.topup-form {
  margin-bottom: 1.5rem;
}

.topup-label {
  display: block;
  color: #888;
  margin-bottom: 0.5rem;
}

.topup-input {
  width: 100%;
  padding: 14px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid var(--color-primary);
  border-radius: 25px;
  color: #fff;
  font-size: 1.1rem;
  text-align: center;
  margin-bottom: 0.75rem;
}

.topup-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.topup-rate {
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-primary);
  background: rgba(0, 212, 255, 0.1);
  padding: 8px 16px;
  border-radius: 6px;
  display: inline-block;
  width: 100%;
}

.topup-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.5rem;
}

.topup-quick-btn {
  flex: 1;
  min-width: 60px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.topup-quick-btn:hover {
  border-color: var(--color-primary);
  background: rgba(0, 212, 255, 0.1);
}

.topup-quick-btn.active {
  border-color: var(--color-primary);
  background: rgba(0, 212, 255, 0.2);
  color: var(--color-primary);
}

.btn-topup-submit {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, var(--color-primary) 0%, #0099cc 100%);
  border: none;
  border-radius: 8px;
  color: #000;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-topup-submit:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 25px rgba(0, 212, 255, 0.4);
}

.delivery-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.btn-invoice-row {
  display: inline-block;
  margin-left: 8px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: transparent;
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #00d4ff;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-invoice-row:hover {
  background: rgba(0, 212, 255, 0.12);
  border-color: #00d4ff;
}

.delivery-delivered {
  background: rgba(0, 255, 100, 0.12);
  color: #00ff64;
  border: 1px solid rgba(0, 255, 100, 0.3);
}

.delivery-pending {
  background: rgba(255, 200, 0, 0.12);
  color: #ffc800;
  border: 1px solid rgba(255, 200, 0, 0.3);
}

.delivery-failed {
  background: rgba(255, 80, 80, 0.12);
  color: #ff6b6b;
  border: 1px solid rgba(255, 80, 80, 0.3);
}

.delivery-na {
  color: #555;
}

/* Withdraw button — full-size, matches balance box */
.btn-withdraw {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, rgba(255, 149, 0, 0.15) 0%, rgba(204, 119, 0, 0.08) 100%);
  border: 1px solid rgba(255, 149, 0, 0.35);
  border-radius: 10px;
  color: #ff9500;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.btn-withdraw svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.btn-withdraw:hover {
  background: linear-gradient(135deg, rgba(255, 149, 0, 0.25) 0%, rgba(204, 119, 0, 0.15) 100%);
  border-color: rgba(255, 149, 0, 0.6);
  box-shadow: 0 4px 20px rgba(255, 149, 0, 0.2);
  transform: translateY(-1px);
}

/* Activity Points button — replaces Withdraw, shows current AT balance */
.btn-activity-tokens {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.15) 0%, rgba(14, 116, 144, 0.08) 100%);
  border: 1px solid rgba(56, 189, 248, 0.4);
  border-radius: 10px;
  color: #7dd3fc;
  padding: 8px 16px;
  gap: 10px;
}
.btn-activity-tokens:hover {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.25) 0%, rgba(14, 116, 144, 0.15) 100%);
  border-color: rgba(125, 211, 252, 0.7);
  box-shadow: 0 4px 20px rgba(56, 189, 248, 0.25);
  transform: translateY(-1px);
}
.btn-activity-tokens .btn-at-label {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  gap: 2px;
}
.btn-activity-tokens .btn-at-title {
  font-size: 0.7rem;
  letter-spacing: 1.5px;
  color: rgba(125, 211, 252, 0.85);
}
.btn-activity-tokens .btn-at-balance {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #bae6fd;
  text-transform: none;
}

.withdraw-gateway-note {
  font-size: 0.72rem;
  line-height: 1.55;
  color: rgba(125, 211, 252, 0.75);
  background: rgba(56, 189, 248, 0.05);
  border: 1px solid rgba(56, 189, 248, 0.18);
  border-radius: 6px;
  padding: 10px 12px;
  margin: 8px 0 14px;
  text-align: center;
}

/* AT modal description */
.at-modal-desc {
  font-size: 0.78rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(56, 189, 248, 0.08);
  border-left: 3px solid rgba(56, 189, 248, 0.5);
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0 0 16px;
}

/* AT modal RATE/MIN pills */
.at-rate-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin: 0 0 16px;
}
.at-rate-pill {
  flex: 1;
  min-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(56, 189, 248, 0.28);
  background: rgba(56, 189, 248, 0.08);
  border-radius: 6px;
}
.at-rate-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(125, 211, 252, 0.85);
}
.at-rate-value {
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: var(--color-primary);
  font-weight: 600;
}

/* Activity Tokens / refund badges for transaction history */
.txn-token-withdrawal,
.txn-token_withdrawal {
  background: rgba(56, 189, 248, 0.2);
  color: #7dd3fc;
}
.txn-token_withdrawal_refund,
.txn-admin_token_grant {
  background: rgba(56, 189, 248, 0.15);
  color: #bae6fd;
}

/* Withdrawal transaction type badge */
.txn-withdrawal {
  background: rgba(255, 149, 0, 0.2);
  color: #ff9500;
}

/* Withdrawal Modal Extras */
.withdraw-modal {
  max-width: 420px;
}

.withdraw-subtitle {
  text-align: center;
  color: #888;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

.payout-method-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #333;
}

.payout-method-tabs button {
  flex: 1;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: none;
  color: #888;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.payout-method-tabs button.active {
  background: rgba(0, 212, 255, 0.15);
  color: var(--color-primary);
}

.payout-method-tabs button:hover:not(.active) {
  background: rgba(255, 255, 255, 0.08);
}

.payout-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.payout-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.payout-form .form-group label {
  color: #888;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.withdraw-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  transition: border-color 0.2s;
}

.withdraw-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.withdraw-error {
  background: rgba(231, 76, 60, 0.15);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #e74c3c;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  text-align: center;
}

.payout-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #333;
  border-radius: 10px;
  margin-bottom: 1.5rem;
}

.payout-summary-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.payout-method-label {
  background: rgba(0, 212, 255, 0.15);
  color: var(--color-primary);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.payout-masked {
  color: #ccc;
  font-family: monospace;
  font-size: 0.9rem;
}

.payout-edit-btn {
  background: none;
  border: 1px solid #444;
  color: #888;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.payout-edit-btn:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.withdraw-balance-hint {
  text-align: center;
  color: #888;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.withdraw-balance-hint strong {
  color: var(--color-primary);
}

.btn-withdraw-submit {
  background: linear-gradient(135deg, #ff9500 0%, #cc7700 100%) !important;
}

.btn-withdraw-submit:hover {
  box-shadow: 0 5px 25px rgba(255, 149, 0, 0.4) !important;
}

.withdraw-confirm-details {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #333;
  border-radius: 10px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}

.confirm-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.confirm-row:last-child {
  border-bottom: none;
}

.confirm-row span {
  color: #888;
}

.confirm-row strong {
  color: #fff;
}

.withdraw-confirm-actions {
  display: flex;
  gap: 10px;
}

.withdraw-confirm-actions .btn-secondary {
  flex: 0 0 auto;
  padding: 14px 24px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid #444;
  border-radius: 8px;
  color: #ccc;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.withdraw-confirm-actions .btn-secondary:hover {
  background: rgba(255, 255, 255, 0.12);
}

.withdraw-confirm-actions .btn-topup-submit {
  flex: 1;
}

.withdraw-success {
  text-align: center;
  padding: 1.5rem 0;
}

.withdraw-success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 255, 100, 0.15);
  color: #00ff64;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  border: 2px solid rgba(0, 255, 100, 0.3);
}

.withdraw-success p {
  color: #ccc;
  margin-bottom: 0.5rem;
}

.withdraw-success strong {
  color: var(--color-primary);
}

.withdraw-success-note {
  color: #666 !important;
  font-size: 0.85rem;
}

/* Inventory button */
.btn-inventory {
  background: linear-gradient(135deg, rgba(46, 204, 113, 0.15) 0%, rgba(39, 174, 96, 0.08) 100%);
  border-color: rgba(46, 204, 113, 0.35);
  color: #2ecc71;
}

.btn-inventory:hover {
  background: linear-gradient(135deg, rgba(46, 204, 113, 0.25) 0%, rgba(39, 174, 96, 0.15) 100%);
  border-color: rgba(46, 204, 113, 0.6);
  box-shadow: 0 4px 20px rgba(46, 204, 113, 0.2);
  transform: translateY(-1px);
}

.btn-inventory svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Inventory Modal */
.inventory-modal {
  max-width: 520px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.inv-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 1rem;
}

.inv-search-input {
  flex: 1;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  font-size: 0.9rem;
  transition: border-color 0.2s;
}

.inv-search-input:focus {
  outline: none;
  border-color: #2ecc71;
}

.inv-search-input::placeholder {
  color: #555;
}

.inv-refresh-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(46, 204, 113, 0.1);
  border: 1px solid rgba(46, 204, 113, 0.3);
  border-radius: 8px;
  color: #2ecc71;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.inv-refresh-btn svg {
  width: 18px;
  height: 18px;
}

.inv-refresh-btn:hover:not(:disabled) {
  background: rgba(46, 204, 113, 0.2);
  border-color: rgba(46, 204, 113, 0.5);
}

.inv-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.inv-spinning {
  animation: inv-spin 0.8s linear infinite;
}

@keyframes inv-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.inv-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 0;
  gap: 16px;
}

.inv-loading-text {
  font-family: var(--font-display);
  font-size: 0.7rem;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.3);
}

.inv-empty {
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 0.9rem;
}

.inv-list {
  flex: 1;
  overflow-y: auto;
  max-height: 45vh;
  margin-bottom: 0.75rem;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
}

.inv-list::-webkit-scrollbar {
  width: 5px;
}

.inv-list::-webkit-scrollbar-track {
  background: transparent;
}

.inv-list::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 3px;
}

.inv-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  margin-bottom: 6px;
  transition: all 0.2s;
}

.inv-item:hover {
  background: rgba(46, 204, 113, 0.05);
  border-color: rgba(46, 204, 113, 0.15);
}

.inv-item-removing {
  opacity: 0.4;
  pointer-events: none;
}

.inv-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.inv-item-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inv-item-blueprint {
  font-size: 0.7rem;
  color: #555;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inv-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: 10px;
}

.inv-item-qty {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2ecc71;
  min-width: 35px;
  text-align: right;
}

.inv-remove-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(231, 76, 60, 0.08);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 6px;
  color: #e74c3c;
  cursor: pointer;
  transition: all 0.2s;
}

.inv-remove-btn svg {
  width: 14px;
  height: 14px;
}

.inv-remove-btn:hover:not(:disabled) {
  background: rgba(231, 76, 60, 0.2);
  border-color: rgba(231, 76, 60, 0.5);
}

.inv-remove-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.inv-footer {
  text-align: center;
  font-size: 0.75rem;
  color: #555;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Remove confirmation overlay (inside modal) */
.inv-confirm-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  z-index: 10;
}

.inv-confirm-box {
  background: linear-gradient(135deg, #1a1a1a 0%, #111 100%);
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  width: 85%;
  max-width: 300px;
  text-align: center;
}

.inv-confirm-title {
  font-family: var(--font-display);
  font-size: 1.1rem;
  color: #e74c3c;
  letter-spacing: 2px;
  margin-bottom: 0.75rem;
}

.inv-confirm-name {
  font-size: 0.95rem;
  color: #eee;
  font-weight: 600;
  margin-bottom: 1rem;
}

.inv-confirm-qty-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.inv-confirm-qty-row label {
  color: #888;
  font-size: 0.85rem;
}

.inv-confirm-input {
  width: 70px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 0.9rem;
  text-align: center;
}

.inv-confirm-input:focus {
  outline: none;
  border-color: #e74c3c;
}

.inv-confirm-max-btn {
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 6px;
  color: #aaa;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.inv-confirm-max-btn:hover {
  border-color: #e74c3c;
  color: #e74c3c;
}

.inv-confirm-actions {
  display: flex;
  gap: 10px;
}

.inv-confirm-actions .btn-secondary {
  flex: 1;
  padding: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid #444;
  border-radius: 8px;
  color: #ccc;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.inv-confirm-actions .btn-secondary:hover {
  background: rgba(255, 255, 255, 0.12);
}

.inv-confirm-delete-btn {
  flex: 1;
  padding: 10px;
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
}

.inv-confirm-delete-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(231, 76, 60, 0.4);
}

.inv-confirm-delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .profile-header {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }

  .profile-user {
    flex-direction: column;
    text-align: center;
  }

  .profile-actions {
    flex-direction: column;
    width: 100%;
  }

  .btn-action-profile {
    width: 100%;
    justify-content: center;
    padding: 12px 18px;
    font-size: 0.75rem;
    height: 44px;
  }

  .btn-logout-icon {
    width: auto;
  }

  .inventory-modal {
    max-width: 95vw;
    max-height: 85vh;
    padding: 1.25rem;
  }

  .inv-item-name {
    font-size: 0.8rem;
  }

  .inv-item-blueprint {
    font-size: 0.6rem;
  }

  .inv-list {
    max-height: 50vh;
  }
}

.account-settings-section {
  background: rgba(10, 10, 20, 0.85);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
}

.ark-settings-msg {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.ark-msg-success {
  color: #00FA9A;
  background: rgba(0, 250, 154, 0.1);
}

.ark-msg-error {
  color: #ff4444;
  background: rgba(255, 68, 68, 0.1);
}

.ark-settings-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: rgba(20, 20, 35, 0.9);
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.ark-settings-toggle:hover {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(30, 30, 50, 0.95);
}

.ark-toggle-icon {
  width: 16px;
  height: 16px;
  color: #888;
  transition: transform 0.2s;
}

.ark-toggle-icon.open {
  transform: rotate(180deg);
}

.ark-settings-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.ark-settings-form input {
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: rgba(5, 5, 15, 0.8);
  color: white;
  font-family: inherit;
  font-size: 0.95rem;
}

.ark-settings-form input:focus {
  border-color: var(--color-primary);
  outline: none;
}

.ark-settings-submit {
  background: var(--color-primary);
  color: white;
  padding: 0.85rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.2s;
  margin-top: 0.25rem;
}

.ark-settings-submit:hover {
  opacity: 0.9;
}

.ark-settings-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ark-settings-toggle-danger {
  border-color: rgba(255, 68, 68, 0.25) !important;
  color: #e55 !important;
}

.ark-settings-toggle-danger:hover {
  border-color: rgba(255, 68, 68, 0.4) !important;
  background: rgba(255, 68, 68, 0.08) !important;
}

.ark-steam-connect-card {
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(20, 20, 35, 0.9);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ark-steam-connect-info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.ark-steam-connect-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.7rem;
  border-radius: 8px;
  border: none;
  background: #1b2838;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.ark-steam-connect-btn:hover {
  background: #2a475e;
}

.ark-delete-confirm-btn {
  width: 100%;
  padding: 0.7rem;
  border-radius: 8px;
  border: none;
  background: #dc2626;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.ark-delete-confirm-btn:hover {
  background: #b91c1c;
}

.ark-delete-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>

